import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Modal,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Drawer,
  Divider,
  Popconfirm,
  Tooltip,
  DatePicker,
  Menu,
  Checkbox,
  Dropdown,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UploadOutlined,
  FileExcelOutlined,
  EyeOutlined,
  PrinterOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TruckOutlined,
  InboxOutlined,
  UserOutlined,
  GlobalOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  SettingOutlined,
  ReloadOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import useDraggableColumns from '../../../components/table/useDraggableColumns';
// import type { UploadProps } from 'antd';
import dayjs from 'dayjs';
import OutboundOrderForm from '../components/OutboundOrderForm';
import apiService from '../../../services/apiService';
import OutboundBulkUploadModal from '../components/BulkUploadModal';
import ReturnOrderModal from '../components/ReturnOrderModal';
import {getHktvMallWaybillHtml, getWaybillHtml, printHktvMallWaybillAuto, printWaybillAuto} from "@utils/pdfUtils.ts";


const { Title, Text } = Typography;
const { Option } = Select;

// 出庫訂單狀態類型
export type OutboundOrderStatus =
    | '待發貨'
    | '處理中'
    | '配貨中'
    | '核對中'
    | '已包裝'
    | '待攬收'
    | '已發貨'
    | '已收貨'
    | '申請退貨'
    | '退貨處理中'
    | '退貨完成';

// 產品行項目接口
export interface OutboundOrderItem {
  id: string;
  productNameEN: string;
  productNameCN: string;
  skuId: string;
  productBarcode?: string;
  quantityPackage: number;
  quantityBaseUnit: number;
  brand: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  unitPrice: number;
  allocateRule: string;
  remark?: string;
  availableStock?: number; // 可用庫存
  isStockInsufficient?: boolean; // 庫存不足標記
}

// 退貨信息接口
export interface ReturnInfo {
  requestDate: string;
  reason: string;
  merchantConfirmation?: string;
  handlingStatus: 'keep' | 'discard' | 'refund' | 'exchange';
  handlingRemark?: string;
  confirmedBy?: string;
  confirmedDate?: string;
  inventoryAdjusted?: boolean;
  adjustmentDate?: string;
}

// 出庫訂單接口
export interface OutboundOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  customerCode: string;
  items: OutboundOrderItem[];
  courier: string;
  receiver: string;
  countryCode: string;
  deliveryAddress: string;
  deliveryTrackingNumber?: string;
  waybillNumber?: string;
  deliveryRemark?: string;
  salesChannel?: string;
  status: OutboundOrderStatus;
  remark?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  totalValue: number;
  hasStockIssues: boolean;
  returnInfo?: ReturnInfo;
  pickUpDate?: string;
}

// 模擬產品數據
const mockProducts = [
  {
    id: 'P001',
    nameEN: 'Wireless Headphones',
    nameCN: '無線耳機',
    skuId: 'WH-001',
    barcodePackage: '1234567890123',
    barcodeBaseUnit: '1234567890124',
    brand: 'TechBrand',
    length: 20,
    width: 15,
    height: 8,
    weight: 0.5,
    unitPrice: 99.99,
    availableStock: 150,
    defaultAllocateRule: 'FIFO'
  },
  {
    id: 'P002',
    nameEN: 'Bluetooth Speaker',
    nameCN: '藍牙音箱',
    skuId: 'BS-002',
    barcodePackage: '2234567890123',
    barcodeBaseUnit: '2234567890124',
    brand: 'AudioMax',
    length: 25,
    width: 12,
    height: 10,
    weight: 1.2,
    unitPrice: 79.99,
    availableStock: 80,
    defaultAllocateRule: 'FIFO'
  },
  {
    id: 'P003',
    nameEN: 'Smart Watch',
    nameCN: '智能手錶',
    skuId: 'SW-003',
    barcodePackage: '3234567890123',
    barcodeBaseUnit: '3234567890124',
    brand: 'SmartTech',
    length: 5,
    width: 4,
    height: 1.5,
    weight: 0.08,
    unitPrice: 199.99,
    availableStock: 25,
    defaultAllocateRule: 'LIFO'
  }
];

// 模擬配送商數據
const mockCouriers = [
  'DHL',
  'FedEx',
  'UPS',
  'TNT',
  '順豐速運',
  '中通快遞',
  '圓通速遞',
  '申通快遞',
  '韻達速遞'
];

// 模擬國家或地區數據
const mockCountryCodes = [
  { value: 'CN', label: '中國 (+86)' },
  { value: 'HK', label: '香港 (+852)' },
  { value: 'MO', label: '澳門 (+853)' },
  { value: 'TW', label: '台灣 (+886)' },
  { value: 'US', label: '美國 (+1)' },
  { value: 'UK', label: '英國 (+44)' },
  { value: 'JP', label: '日本 (+81)' },
  { value: 'KR', label: '韓國 (+82)' },
  { value: 'SG', label: '新加坡 (+65)' },
  { value: 'MY', label: '馬來西亞 (+60)' },
  { value: 'TH', label: '泰國 (+66)' },
  { value: 'AU', label: '澳洲 (+61)' },
  { value: 'CA', label: '加拿大 (+1)' },
  { value: 'FR', label: '法國 (+33)' },
  { value: 'DE', label: '德國 (+49)' },
];

// 分配規則選項
const allocateRules = [
  { value: 'FIFO', label: 'FIFO (先進先出)' },
  { value: 'LIFO', label: 'LIFO (後進先出)' },
  { value: 'FEFO', label: 'FEFO (先到期先出)' },
  { value: 'MANUAL', label: '手動分配' },
];

// 模擬出庫訂單數據（暫不使用）
/* const mockOutboundOrders: OutboundOrder[] = [
  {
    id: 'OB001',
    orderNumber: 'OUT-2024-001',
    customerName: '客戶A',
    customerCode: 'CUST001',
    items: [
      {
        id: 'item1',
        productNameEN: 'Wireless Headphones',
        productNameCN: '無線耳機',
        skuId: 'WH-001',
        barcodePackage: '1234567890123',
        barcodeBaseUnit: '1234567890124',
        quantityPackage: 10,
        quantityBaseUnit: 10,
        brand: 'TechBrand',
        length: 20,
        width: 15,
        height: 8,
        weight: 0.5,
        unitPrice: 99.99,
        allocateRule: 'FIFO',
        availableStock: 150,
        isStockInsufficient: false
      }
    ],
    courier: 'DHL',
    receiver: '李先生',
    countryCode: 'CN',
    deliveryAddress: '廣東省深圳市南山區科技園',
    deliveryTrackingNumber: 'DHL123456789',
    salesChannel: 'HKTV Mall',
    status: '已發貨',
    totalValue: 999.90,
    hasStockIssues: false,
    createdAt: '2024-01-15 10:30:00',
    updatedAt: '2024-01-15 16:20:00',
    createdBy: '張三'
  },
  {
    id: 'OB002',
    orderNumber: 'OUT-2024-002',
    customerName: '客戶B',
    customerCode: 'CUST002',
    items: [
      {
        id: 'item2',
        productNameEN: 'Smart Watch',
        productNameCN: '智能手錶',
        skuId: 'SW-003',
        barcodePackage: '3234567890123',
        barcodeBaseUnit: '3234567890124',
        quantityPackage: 50,
        quantityBaseUnit: 50,
        brand: 'SmartTech',
        length: 5,
        width: 4,
        height: 1.5,
        weight: 0.08,
        unitPrice: 199.99,
        allocateRule: 'LIFO',
        availableStock: 25,
        isStockInsufficient: true
      }
    ],
    courier: '順豐速運',
    receiver: '王女士',
    countryCode: 'CN',
    deliveryAddress: '上海市浦東新區陸家嘴金融中心',
    salesChannel: 'Shopify',
    status: '待發貨',
    totalValue: 9999.50,
    hasStockIssues: true,
    createdAt: '2024-01-15 14:20:00',
    updatedAt: '2024-01-15 14:20:00',
    createdBy: '李四'
  },
  {
    id: 'OB003',
    orderNumber: 'OUT-2024-003',
    customerName: '客戶C',
    customerCode: 'CUST003',
    items: [
      {
        id: 'item3',
        productNameEN: 'Bluetooth Speaker',
        productNameCN: '藍牙音箱',
        skuId: 'BS-002',
        barcodePackage: '2234567890123',
        barcodeBaseUnit: '2234567890124',
        quantityPackage: 5,
        quantityBaseUnit: 5,
        brand: 'AudioMax',
        length: 25,
        width: 12,
        height: 10,
        weight: 1.2,
        unitPrice: 79.99,
        allocateRule: 'FIFO',
        availableStock: 80,
        isStockInsufficient: false
      }
    ],
    courier: 'FedEx',
    receiver: '陳先生',
    countryCode: 'CN',
    deliveryAddress: '北京市朝陽區國貿中心',
    deliveryTrackingNumber: 'FDX987654321',
    salesChannel: 'Amazon',
    status: '已收貨',
    totalValue: 399.95,
    hasStockIssues: false,
    createdAt: '2024-01-10 09:15:00',
    updatedAt: '2024-01-14 15:30:00',
    createdBy: '王五'
  },
  {
    id: 'OB004',
    orderNumber: 'OUT-2024-004',
    customerName: '客戶D',
    customerCode: 'CUST004',
    items: [
      {
        id: 'item4',
        productNameEN: 'USB-C Cable',
        productNameCN: 'USB-C 數據線',
        skuId: 'UC-004',
        barcodePackage: '4234567890123',
        barcodeBaseUnit: '4234567890124',
        quantityPackage: 20,
        quantityBaseUnit: 20,
        brand: 'CablePro',
        length: 1,
        width: 1,
        height: 1,
        weight: 0.05,
        unitPrice: 9.99,
        allocateRule: 'FIFO',
        availableStock: 500,
        isStockInsufficient: false
      }
    ],
    courier: 'UPS',
    receiver: '趙先生',
    countryCode: 'CN',
    deliveryAddress: '杭州市西湖區文三路',
    deliveryTrackingNumber: '',
    salesChannel: 'eBay',
    status: '處理中',
    totalValue: 199.80,
    hasStockIssues: false,
    createdAt: '2024-01-16 09:00:00',
    updatedAt: '2024-01-16 09:30:00',
    createdBy: '王六'
  },
  {
    id: 'OB005',
    orderNumber: 'OUT-2024-005',
    customerName: '客戶E',
    customerCode: 'CUST005',
    items: [
      {
        id: 'item5',
        productNameEN: 'Laptop Stand',
        productNameCN: '筆電支架',
        skuId: 'LS-005',
        barcodePackage: '5234567890123',
        barcodeBaseUnit: '5234567890124',
        quantityPackage: 10,
        quantityBaseUnit: 10,
        brand: 'ErgoTech',
        length: 30,
        width: 25,
        height: 5,
        weight: 0.9,
        unitPrice: 39.99,
        allocateRule: 'FIFO',
        availableStock: 60,
        isStockInsufficient: false
      }
    ],
    courier: '中通快遞',
    receiver: '孫女士',
    countryCode: 'CN',
    deliveryAddress: '南京市秦淮區中山南路',
    deliveryTrackingNumber: '',
    salesChannel: 'Taobao',
    status: '配貨中',
    totalValue: 399.90,
    hasStockIssues: false,
    createdAt: '2024-01-16 10:00:00',
    updatedAt: '2024-01-16 10:20:00',
    createdBy: '周一'
  },
  {
    id: 'OB006',
    orderNumber: 'OUT-2024-006',
    customerName: '客戶F',
    customerCode: 'CUST006',
    items: [
      {
        id: 'item6',
        productNameEN: 'Mouse Pad',
        productNameCN: '滑鼠墊',
        skuId: 'MP-006',
        barcodePackage: '6234567890123',
        barcodeBaseUnit: '6234567890124',
        quantityPackage: 30,
        quantityBaseUnit: 30,
        brand: 'SoftPad',
        length: 25,
        width: 20,
        height: 0.5,
        weight: 0.12,
        unitPrice: 5.99,
        allocateRule: 'FEFO',
        availableStock: 200,
        isStockInsufficient: false
      }
    ],
    courier: '圓通速遞',
    receiver: '周先生',
    countryCode: 'CN',
    deliveryAddress: '重慶市渝中區解放碑',
    deliveryTrackingNumber: '',
    status: '核對中',
    totalValue: 179.70,
    hasStockIssues: false,
    createdAt: '2024-01-16 11:00:00',
    updatedAt: '2024-01-16 11:10:00',
    createdBy: '吳二'
  },
  {
    id: 'OB007',
    orderNumber: 'OUT-2024-007',
    customerName: '客戶G',
    customerCode: 'CUST007',
    items: [
      {
        id: 'item7',
        productNameEN: 'Keyboard',
        productNameCN: '鍵盤',
        skuId: 'KB-007',
        barcodePackage: '7234567890123',
        barcodeBaseUnit: '7234567890124',
        quantityPackage: 12,
        quantityBaseUnit: 12,
        brand: 'KeyMaster',
        length: 45,
        width: 15,
        height: 4,
        weight: 0.7,
        unitPrice: 49.99,
        allocateRule: 'FIFO',
        availableStock: 40,
        isStockInsufficient: false
      }
    ],
    courier: '申通快遞',
    receiver: '錢先生',
    countryCode: 'CN',
    deliveryAddress: '天津市和平區濱江道',
    deliveryTrackingNumber: '',
    status: '已包裝',
    totalValue: 599.88,
    hasStockIssues: false,
    createdAt: '2024-01-16 12:00:00',
    updatedAt: '2024-01-16 12:20:00',
    createdBy: '鄭三'
  },
  {
    id: 'OB008',
    orderNumber: 'OUT-2024-008',
    customerName: '客戶H',
    customerCode: 'CUST008',
    items: [
      {
        id: 'item8',
        productNameEN: 'Webcam',
        productNameCN: '攝像頭',
        skuId: 'WC-008',
        barcodePackage: '8234567890123',
        barcodeBaseUnit: '8234567890124',
        quantityPackage: 8,
        quantityBaseUnit: 8,
        brand: 'CamPlus',
        length: 8,
        width: 5,
        height: 4,
        weight: 0.2,
        unitPrice: 59.99,
        allocateRule: 'FIFO',
        availableStock: 100,
        isStockInsufficient: false
      }
    ],
    courier: '韻達速遞',
    receiver: '吳女士',
    countryCode: 'CN',
    deliveryAddress: '成都市武侯區人民南路',
    deliveryTrackingNumber: '',
    status: '待攬收',
    totalValue: 479.92,
    hasStockIssues: false,
    createdAt: '2024-01-16 13:00:00',
    updatedAt: '2024-01-16 13:15:00',
    createdBy: '錢七'
  },
  {
    id: 'OB009',
    orderNumber: 'OUT-2024-009',
    customerName: '客戶I',
    customerCode: 'CUST009',
    items: [
      {
        id: 'item9',
        productNameEN: 'External HDD',
        productNameCN: '移動硬碟',
        skuId: 'EH-009',
        barcodePackage: '9234567890123',
        barcodeBaseUnit: '9234567890124',
        quantityPackage: 6,
        quantityBaseUnit: 6,
        brand: 'StoreX',
        length: 12,
        width: 8,
        height: 2,
        weight: 0.25,
        unitPrice: 79.99,
        allocateRule: 'FIFO',
        availableStock: 30,
        isStockInsufficient: false
      }
    ],
    courier: 'DHL',
    receiver: '鄒先生',
    countryCode: 'CN',
    deliveryAddress: '武漢市江漢區江漢路',
    deliveryTrackingNumber: 'DHL999999999',
    status: '申請退貨',
    totalValue: 479.94,
    hasStockIssues: false,
    createdAt: '2024-01-12 10:00:00',
    updatedAt: '2024-01-16 14:00:00',
    createdBy: '佘八'
  },
  {
    id: 'OB010',
    orderNumber: 'OUT-2024-010',
    customerName: '客戶J',
    customerCode: 'CUST010',
    items: [
      {
        id: 'item10',
        productNameEN: 'HDMI Cable',
        productNameCN: 'HDMI 線',
        skuId: 'HC-010',
        barcodePackage: '1334567890123',
        barcodeBaseUnit: '1334567890124',
        quantityPackage: 15,
        quantityBaseUnit: 15,
        brand: 'CablePro',
        length: 2,
        width: 1,
        height: 1,
        weight: 0.1,
        unitPrice: 12.99,
        allocateRule: 'FIFO',
        availableStock: 100,
        isStockInsufficient: false
      }
    ],
    courier: 'FedEx',
    receiver: '雷女士',
    countryCode: 'CN',
    deliveryAddress: '合肥市包河區徽州大道',
    deliveryTrackingNumber: 'FDX111111111',
    status: '退貨處理中',
    totalValue: 194.85,
    hasStockIssues: false,
    createdAt: '2024-01-11 10:00:00',
    updatedAt: '2024-01-16 15:00:00',
    createdBy: '張九'
  },
  {
    id: 'OB011',
    orderNumber: 'OUT-2024-011',
    customerName: '客戶K',
    customerCode: 'CUST011',
    items: [
      {
        id: 'item11',
        productNameEN: 'USB Hub',
        productNameCN: 'USB 集線器',
        skuId: 'UH-011',
        barcodePackage: '2334567890123',
        barcodeBaseUnit: '2334567890124',
        quantityPackage: 9,
        quantityBaseUnit: 9,
        brand: 'Hubman',
        length: 10,
        width: 4,
        height: 2,
        weight: 0.15,
        unitPrice: 24.99,
        allocateRule: 'FIFO',
        availableStock: 70,
        isStockInsufficient: false
      }
    ],
    courier: 'TNT',
    receiver: '常先生',
    countryCode: 'CN',
    deliveryAddress: '福州市鼓樓區東街口',
    deliveryTrackingNumber: 'TNT222222222',
    status: '退貨完成',
    totalValue: 224.91,
    hasStockIssues: false,
    createdAt: '2024-01-10 10:00:00',
    updatedAt: '2024-01-16 16:00:00',
    createdBy: '李十'
  }
]; */

const OutboundOrderPage: React.FC = () => {
  const [outboundOrders, setOutboundOrders] = useState<OutboundOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OutboundOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OutboundOrder | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [editingOrder, setEditingOrder] = useState<OutboundOrder | null>(null);
  const [bulkUploadVisible, setBulkUploadVisible] = useState(false);
  const [returnModalVisible, setReturnModalVisible] = useState(false);
  const [returningOrder, setReturningOrder] = useState<OutboundOrder | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  
  // Update Pick Up Date modal state
  const [updatePickUpDateModalVisible, setUpdatePickUpDateModalVisible] = useState(false);
  const [updatePickUpDateRange, setUpdatePickUpDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [updatePickUpDateLoading, setUpdatePickUpDateLoading] = useState(false);

  // 出庫訂單表格排序狀態
  const [outboundSortField, setOutboundSortField] = useState<string>('');
  const [outboundSortOrder, setOutboundSortOrder] = useState<'asc' | 'desc' | ''>('');
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [customers, setCustomers] = useState<Array<{ value: string; label: string; code: string }>>([]);
  const [runTags, setRunTags] = useState<Array<{ id: string; tagName: string; storeCodes: string[]; warehouseIds: string[] }>>([]);
  const [hktvAccounts, setHktvAccounts] = useState<Array<{ storeCode: string; status: string }>>([]);

  useEffect(() => {
    (async () => {
      try {
        const res: any = await apiService.getWarehouses({ limit: 1000 });
        const list: any[] = res?.data || res?.warehouses || [];
        const mapped = list.map((w: any) => ({ id: w.id || w.code || w.warehouseId, name: w.name || w.warehouseName || w.code }));
        setWarehouses(mapped);
      } catch {}
    })();
  }, []);

  // Load run tags with warehouse IDs
  useEffect(() => {
    (async () => {
      try {
        const res: any = await apiService.getRunTagsWithWarehouseIds();
        if (res?.success && Array.isArray(res?.data)) {
          setRunTags(res.data);
        }
      } catch (error) {
        console.error('Failed to load run tags:', error);
      }
    })();
  }, []);

  // Load customers from API
  useEffect(() => {
    (async () => {
      try {
        const res: any = await apiService.getCustomers({ limit: 1000, isActive: true });
        const list: any[] = res?.customers || res?.data || [];
        const mapped = list.map((c: any) => ({
          value: c.id,
          label: c.name,
          code: c.id
        }));
        setCustomers(mapped);
      } catch (error) {
        console.error('Failed to load customers:', error);
        // Fallback to empty array if API fails
        setCustomers([]);
      }
    })();
  }, []);

  // Load HKTV accounts from API
  useEffect(() => {
    (async () => {
      try {
        const result: any = await apiService.get('/integrations/hktvmall/accounts');
        if (result?.success) {
          const list: any[] = result?.data || [];
          setHktvAccounts(list);
        }
      } catch (error) {
        console.error('Failed to load HKTV accounts:', error);
        setHktvAccounts([]);
      }
    })();
  }, []);


  // HKTVmall 運單自動打印（同時生成PDF）
  const handleHktvMallDownloadPDF = async (orderNumber: string, waybillNumber: string) => {
    // 自動打印運單並同時生成PDF下載
    await printHktvMallWaybillAuto(orderNumber, waybillNumber);
  };

  // 普通運單自動打印（同時生成PDF）
  const handleDownloadPDF = async (orderNumber: string, waybillNumber: string) => {
    // 自動打印運單並同時生成PDF下載
    await printWaybillAuto(orderNumber, waybillNumber);
  };


  // 出庫訂單表格列配置狀態
  const [outboundColumnSettings, setOutboundColumnSettings] = useState(() => {
    const saved = localStorage.getItem('outbound-order-columns-config');
    const version = localStorage.getItem('outbound-order-columns-version');

    // 檢查版本，如果不是最新版本則重置配置
    if (version !== '2024-01-v5' || !saved) {
      const defaultConfig = {
        orderNumber: { visible: true, order: 0 },
        customerName: { visible: true, order: 1 },
        customerCode: { visible: true, order: 2 },
        receiver: { visible: true, order: 3 },
        deliveryAddress: { visible: true, order: 4 },
        courier: { visible: true, order: 5 },
        salesChannel: { visible: true, order: 6 },
        pickUpDate: { visible: true, order: 7 },
        trackingNumber: { visible: false, order: 8 },
        waybill: { visible: true, order: 9 },
        status: { visible: true, order: 10 },
        totalValue: { visible: true, order: 11 },
        createdAt: { visible: true, order: 12 },
        actions: { visible: true, order: 13 }
      };
      localStorage.setItem('outbound-order-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('outbound-order-columns-version', '2024-01-v5');
      return defaultConfig;
    }

    try {
      return JSON.parse(saved);
    } catch {
      const defaultConfig = {
        orderNumber: { visible: true, order: 0 },
        customerName: { visible: true, order: 1 },
        customerCode: { visible: true, order: 2 },
        receiver: { visible: true, order: 3 },
        deliveryAddress: { visible: true, order: 4 },
        courier: { visible: true, order: 5 },
        salesChannel: { visible: true, order: 6 },
        pickUpDate: { visible: true, order: 7 },
        trackingNumber: { visible: false, order: 8 },
        waybill: { visible: true, order: 9 },
        status: { visible: true, order: 10 },
        totalValue: { visible: true, order: 11 },
        createdAt: { visible: true, order: 12 },
        actions: { visible: true, order: 13 }
      };
      return defaultConfig;
    }
  });

  // 處理出庫訂單列排序
  const handleOutboundColumnSort = (columnKey: string) => {
    if (outboundSortField === columnKey) {
      setOutboundSortOrder(outboundSortOrder === 'asc' ? 'desc' : outboundSortOrder === 'desc' ? '' : 'asc');
      if (outboundSortOrder === 'desc') {
        setOutboundSortField('');
      }
    } else {
      setOutboundSortField(columnKey);
      setOutboundSortOrder('asc');
    }
  };

  // 獲取出庫訂單排序圖標
  const getOutboundSortIcon = (columnKey: string) => {
    if (outboundSortField !== columnKey) return null;
    return outboundSortOrder === 'asc' ?
        <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
        <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  // 篩選狀態
  const [filters, setFilters] = useState({
    status: '',
    customer: '',
    hasStockIssues: '',
    searchText: '',
    salesChannel: '',
    pickUpDateRange: undefined as [string, string] | undefined,
    storeId: [] as string[],
    runTagId: ''
  });

  // 列設置下拉菜單可見狀態
  const [outboundColumnSettingsVisible, setOutboundColumnSettingsVisible] = useState(false);

  // 展開收起控制
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const toggleCollapsed = () => setCollapsed(prev => !prev);

  // 受控分頁（後端返回 total）
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);

  // 從後端載入出庫訂單
  const mapBackendStatusToZh = (s?: string): OutboundOrderStatus => {
    switch ((s || '').toUpperCase()) {
      case 'PENDING': return '待發貨';
      case 'PROCESSING': return '處理中';
      case 'ALLOCATING': return '配貨中';
      case 'VERIFYING': return '核對中';
      case 'PACKED': return '已包裝';
      case 'PICKUP_PENDING': return '待攬收';
      case 'SHIPPED': return '已發貨';
      case 'RECEIVED': return '已收貨';
      case 'RETURN_REQUESTED': return '申請退貨';
      case 'RETURN_PROCESSING': return '退貨處理中';
      case 'RETURN_COMPLETED': return '退貨完成';
      default: return '待發貨';
    }
  };

  const loadOutboundOrders = async () => {
    try {
      setLoading(true);
      const resp: any = await apiService.getOutboundOrders({
        search: filters.searchText || undefined,
        status: filters.status ? mapZhStatusToBackend(filters.status as OutboundOrderStatus) : undefined,
        salesChannel: filters.salesChannel || undefined,
        pickUpDateStart: filters.pickUpDateRange?.[0] || undefined,
        pickUpDateEnd: filters.pickUpDateRange?.[1] || undefined,
        storeId: filters.storeId.length > 0 ? filters.storeId.join(',') : undefined,
        hasStockIssues: filters.hasStockIssues || undefined,
        runTagId: filters.runTagId || undefined,
        sortField: 'created_at',
        sortOrder: 'desc',
        page: currentPage,
        limit: pageSize
      });
      const rows: any[] = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
      const mapped: OutboundOrder[] = rows.map((o: any) => ({
        id: o.id,
        orderNumber: o.outboundNumber || o.outbound_number || o.orderNumber || '-',
        customerName: o.customerName || o.customer_id || '-',
        customerCode: o.customerCode || o.customer_id || '-',
        items: Array.isArray(o.items) ? o.items : [],
        courier: o.courier || o.deliveryCompany || '-',
        receiver: o.receiver || '-',
        countryCode: o.countryCode || 'CN',
        deliveryAddress: o.deliveryAddress || '-',
        deliveryTrackingNumber: o.trackingNumber || o.deliveryTrackingNumber || '',
        waybillNumber: o.waybillNumber || o.waybill_number || o.trackingNumber || o.deliveryTrackingNumber || '',
        deliveryRemark: o.deliveryRemark || o.remark || '',
        status: mapBackendStatusToZh(o.status),
        remark: o.notes || o.remark,
        createdAt: o.createdAt || o.created_at || new Date().toISOString(),
        updatedAt: o.updatedAt || o.updated_at || new Date().toISOString(),
        createdBy: o.createdBy || o.created_by || '-',
        totalValue: Number(o.totalAmount ?? o.total_value ?? 0),
        hasStockIssues: Boolean(o.hasStockIssues || false),
        returnInfo: undefined,
        pickUpDate: o.pickUpDate || undefined,
      }));
      setOutboundOrders(mapped);
      setFilteredOrders(mapped);
      const respTotal = resp?.pagination?.total ?? resp?.total ?? resp?.totalCount ?? resp?.count ?? resp?.meta?.total ?? resp?.meta?.pagination?.total ?? mapped.length;
      setTotal(Number(respTotal) || mapped.length);
    } catch (e) {
      console.error('載入出庫訂單失敗:', e);
      message.error('載入出庫訂單失敗');
    } finally {
      setLoading(false);
    }
  };

  // 將中文狀態映射回後端狀態碼
  const mapZhStatusToBackend = (s: OutboundOrderStatus): string => {
    switch (s) {
      case '待發貨': return 'PENDING';
      case '處理中': return 'PROCESSING';
      case '配貨中': return 'ALLOCATING';
      case '核對中': return 'VERIFYING';
      case '已包裝': return 'PACKED';
      case '待攬收': return 'PICKUP_PENDING';
      case '已發貨': return 'SHIPPED';
      case '已收貨': return 'RECEIVED';
      case '申請退貨': return 'RETURN_REQUESTED';
      case '退貨處理中': return 'RETURN_PROCESSING';
      case '退貨完成': return 'RETURN_COMPLETED';
      default: return 'PENDING';
    }
  };

  useEffect(() => {
    loadOutboundOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filters.searchText, filters.status, filters.customer, filters.salesChannel, filters.hasStockIssues, filters.pickUpDateRange, filters.storeId, filters.runTagId]);

  // 統計數據
  const statistics = React.useMemo(() => {
    const total = filteredOrders.length;
    const pending = filteredOrders.filter(order => order.status === '待發貨').length;
    const processing = filteredOrders.filter(order =>
        ['處理中', '配貨中', '核對中', '已包裝', '待攬收'].includes(order.status)
    ).length;
    const shipped = filteredOrders.filter(order => order.status === '已發貨').length;
    const stockIssues = filteredOrders.filter(order => order.hasStockIssues).length;

    return { total, pending, processing, shipped, stockIssues };
  }, [filteredOrders]);

  // 受控分頁改為後端查詢：前端不再重覆過濾，直接使用後端返回的結果
  useEffect(() => {
    setFilteredOrders(outboundOrders);
  }, [outboundOrders]);

  // 獲取狀態標籤顏色
  const getStatusColor = (status: OutboundOrderStatus) => {
    switch (status) {
      case '待發貨':
        return 'orange';
      case '處理中':
        return 'blue';
      case '配貨中':
        return 'cyan';
      case '核對中':
        return 'purple';
      case '已包裝':
        return 'magenta';
      case '待攬收':
        return 'gold';
      case '已發貨':
        return 'green';
      case '已收貨':
        return 'success';
      case '申請退貨':
        return 'volcano';
      case '退貨處理中':
        return 'red';
      case '退貨完成':
        return 'default';
      default:
        return 'default';
    }
  };

  // 獲取狀態圖標
  const getStatusIcon = (status: OutboundOrderStatus) => {
    switch (status) {
      case '待發貨':
        return <ClockCircleOutlined />;
      case '處理中':
        return <EditOutlined />;
      case '配貨中':
        return <ShoppingCartOutlined />;
      case '核對中':
        return <EyeOutlined />;
      case '已包裝':
        return <InboxOutlined />;
      case '待攬收':
        return <TruckOutlined />;
      case '已發貨':
        return <CheckCircleOutlined />;
      case '已收貨':
        return <CheckCircleOutlined />;
      case '申請退貨':
        return <WarningOutlined />;
      case '退貨處理中':
        return <EditOutlined />;
      case '退貨完成':
        return <CheckCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  // 處理新增訂單
  const handleAddOrder = () => {
    setEditingOrder(null);
    setFormVisible(true);
  };

  // 處理查看訂單詳情
  const handleViewOrder = async (order: OutboundOrder) => {
    try {
      // 調用 detail API 獲取完整數據
      const response: any = await apiService.getOutboundOrder(order.id);
      const orderDetail = response?.data || response;

      if (orderDetail && orderDetail.id) {
        const mappedOrder: OutboundOrder = {
          id: orderDetail.id,
          orderNumber: orderDetail.orderNumber || orderDetail.outboundNumber,
          customerName: orderDetail.customerName || orderDetail.customerCode,
          customerCode: orderDetail.customerCode,
          items: Array.isArray(orderDetail.items) ? orderDetail.items : [],
          courier: orderDetail.courier || orderDetail.deliveryCompany,
          receiver: orderDetail.receiver,
          countryCode: orderDetail.countryCode || 'CN',
          deliveryAddress: orderDetail.deliveryAddress,
          deliveryTrackingNumber: orderDetail.deliveryTrackingNumber || orderDetail.trackingNumber,
          deliveryRemark: orderDetail.deliveryRemark || orderDetail.remark,
          status: orderDetail.status ? mapBackendStatusToZh(orderDetail.status) : order.status,
          remark: orderDetail.remark,
          createdAt: orderDetail.createdAt,
          updatedAt: orderDetail.updatedAt,
          createdBy: orderDetail.createdBy,
          totalValue: Number(orderDetail.totalValue ?? orderDetail.total_amount ?? 0),
          hasStockIssues: Boolean(orderDetail.hasStockIssues),
          returnInfo: order.returnInfo,
          pickUpDate: orderDetail.pickUpDate || order.pickUpDate,
        };
        setSelectedOrder(mappedOrder);
      } else {
        // 降級處理：使用列表數據
        setSelectedOrder(order);
      }
      setDrawerVisible(true);
    } catch (error) {
      console.error('Failed to load order details:', error);
      // 降級處理：使用列表數據
      setSelectedOrder(order);
      setDrawerVisible(true);
    }
  };

  // 處理編輯訂單
  const handleEditOrder = (order: OutboundOrder) => {
    setEditingOrder(order);
    setFormVisible(true);
  };

  // 處理表單提交
  const handleFormSubmit = async (formData: any) => {
    try {
      if (editingOrder) {
        // 調用後端更新
        const payload = {
          customerCode: formData.customerCode,
          receiver: formData.receiver,
          deliveryAddress: formData.deliveryAddress,
          courier: formData.courier,
          deliveryTrackingNumber: formData.deliveryTrackingNumber,
          status: formData.status,
          remark: formData.remark,
          totalValue: formData.totalValue,
          items: formData.items,
          warehouseId: formData.warehouseId,
        };
        const resp: any = await apiService.updateOutboundOrder(editingOrder.id || editingOrder.orderNumber, payload);
        // 回顯：以後端返回詳情替換當前行
        const updated = resp?.data || resp;
        if (updated && updated.id) {
          const mapped: OutboundOrder = {
            id: updated.id,
            orderNumber: updated.orderNumber || updated.outboundNumber || editingOrder.orderNumber,
            customerName: updated.customerName || editingOrder.customerName,
            customerCode: updated.customerCode || editingOrder.customerCode,
            items: Array.isArray(updated.items) ? updated.items : editingOrder.items,
            courier: updated.courier || updated.deliveryCompany || editingOrder.courier,
            receiver: updated.receiver || editingOrder.receiver,
            countryCode: updated.countryCode || editingOrder.countryCode,
            deliveryAddress: updated.deliveryAddress || editingOrder.deliveryAddress,
            deliveryTrackingNumber: updated.deliveryTrackingNumber || updated.trackingNumber || editingOrder.deliveryTrackingNumber,
            deliveryRemark: updated.deliveryRemark || updated.remark || editingOrder.deliveryRemark,
            status: updated.status ? mapBackendStatusToZh(updated.status) : editingOrder.status,
            remark: updated.remark || editingOrder.remark,
            createdAt: updated.createdAt || editingOrder.createdAt,
            updatedAt: updated.updatedAt || new Date().toISOString(),
            createdBy: updated.createdBy || editingOrder.createdBy,
            totalValue: Number(updated.totalValue ?? updated.total_amount ?? editingOrder.totalValue ?? 0),
            hasStockIssues: Boolean(updated.hasStockIssues ?? editingOrder.hasStockIssues),
            returnInfo: editingOrder.returnInfo,
            pickUpDate: updated.pickUpDate || editingOrder.pickUpDate,
          };
          setOutboundOrders(prev => prev.map(o => (o.id === mapped.id ? mapped : o)));
          message.success('出庫訂單已更新');
        }
        setFormVisible(false);
        setEditingOrder(null);
      } else {
        // 調用後端新增
        const payload = {
          customerCode: formData.customerCode,
          receiver: formData.receiver,
          deliveryAddress: formData.deliveryAddress,
          courier: formData.courier,
          deliveryTrackingNumber: formData.deliveryTrackingNumber,
          status: formData.status,
          remark: formData.remark,
          totalValue: formData.totalValue,
          items: formData.items,
          warehouseId: formData.warehouseId,
        };
        const resp: any = await apiService.createOutboundOrder(payload);
        const created = resp?.data || resp;
        if (created && created.id) {
          const mapped: OutboundOrder = {
            id: created.id,
            orderNumber: created.orderNumber || created.outboundNumber,
            customerName: created.customerName || formData.customerName,
            customerCode: created.customerCode || formData.customerCode,
            items: Array.isArray(created.items) ? created.items : formData.items,
            courier: created.courier || created.deliveryCompany || formData.courier,
            receiver: created.receiver || formData.receiver,
            countryCode: created.countryCode || formData.countryCode,
            deliveryAddress: created.deliveryAddress || formData.deliveryAddress,
            deliveryTrackingNumber: created.deliveryTrackingNumber || created.trackingNumber || formData.deliveryTrackingNumber,
            deliveryRemark: created.deliveryRemark || created.remark || formData.deliveryRemark,
            status: mapBackendStatusToZh(created.status) || formData.status,
            remark: created.remark || formData.remark,
            createdAt: created.createdAt || new Date().toISOString(),
            updatedAt: created.updatedAt || new Date().toISOString(),
            createdBy: created.createdBy || 'current_user',
            totalValue: Number(created.totalValue ?? created.total_amount ?? formData.totalValue ?? 0),
            hasStockIssues: Boolean(created.hasStockIssues ?? false),
            returnInfo: undefined,
            pickUpDate: created.pickUpDate || undefined,
          };
          setOutboundOrders(prev => [mapped, ...prev]);
          message.success('出庫訂單已建立');
        }
        setFormVisible(false);
      }
    } catch (error) {
      console.error('Failed to submit outbound order:', error);
      message.error('提交出庫訂單失敗');
    }
  };

  // 處理訂單刪除
  const handleDeleteOrder = async (order: OutboundOrder) => {
    try {
      // 單次刪除：嚴格使用主鍵 ID（後端同時支持 id 或 outbound_number）
      const ident = order.id;
      const resp: any = await apiService.deleteOutboundOrder(ident);
      if (resp && typeof resp === 'object' && 'success' in resp && resp.success === false) {
        throw new Error(resp.message || 'Delete failed');
      }
      await loadOutboundOrders();
      message.success('出庫訂單刪除成功');
    } catch (error: any) {
      console.error('刪除出庫訂單失敗:', error);
      // 若後端回覆已不存在，視為最終態一致，刷新列表並提示成功
      if (String(error?.message || '').toLowerCase().includes('outbound order not found')) {
        await loadOutboundOrders();
        message.success('出庫訂單刪除成功');
        return;
      }
      message.error(error?.message || '刪除失敗，請重試');
    }
  };

  // 導出功能 - 後端生成，帶當前篩選條件且全量導出
  const handleExportExcel = async () => {
    try {
      message.loading('正在根據條件導出，請稍候...', 0.6);
      const params: any = {
        search: filters.searchText || undefined,
        status: filters.status ? mapZhStatusToBackend(filters.status as OutboundOrderStatus) : undefined,
        salesChannel: filters.salesChannel || undefined,
        pickUpDateStart: filters.pickUpDateRange?.[0] || undefined,
        pickUpDateEnd: filters.pickUpDateRange?.[1] || undefined,
        storeId: filters.storeId.length > 0 ? filters.storeId.join(',') : undefined,
        hasStockIssues: filters.hasStockIssues || undefined,
        runTagId: filters.runTagId || undefined,
        sortField: 'created_at',
        sortOrder: 'desc'
      };
      const { blob, fileName } = await apiService.exportOutboundOrdersCSV(params);
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
      message.success('出庫訂單已按當前條件導出');
    } catch (error) {
      console.error('導出失敗:', error);
      message.error('導出失敗，請稍後重試');
    }
  };

  // Handler for Update Pick Up Date button
  const handleUpdatePickUpDate = async () => {
    if (!updatePickUpDateRange) {
      message.error('請選擇取貨日期範圍');
      return;
    }

    const startDate = updatePickUpDateRange[0];
    const endDate = updatePickUpDateRange[1];
    
    // Validate max 7 days range
    const daysDiff = endDate.diff(startDate, 'days');
    if (daysDiff > 7) {
      message.error('日期範圍不能超過7天');
      return;
    }

    try {
      setUpdatePickUpDateLoading(true);
      
      const params: any = {
        pickUpDateStart: startDate.format('YYYY-MM-DD'),
        pickUpDateEnd: endDate.format('YYYY-MM-DD')
      };
      
      // 如果有選擇 Run Tag，則帶上 runTagId
      if (filters.runTagId) {
        params.runTagId = filters.runTagId;
        console.log('Update Pick Up Date with Run Tag:', filters.runTagId);
      }

      // Call backend API - 現在立即返回 taskId
      const response: any = await apiService.updatePickUpDateRange(params);
      
      if (response.success && response.taskId) {
        const taskId = response.taskId;
        
        message.info(`任務已創建，正在後台處理中...`);
        
        // Close modal immediately
        setUpdatePickUpDateModalVisible(false);
        setUpdatePickUpDateRange(null);
        
        // 輪詢檢查任務狀態
        const pollInterval = setInterval(async () => {
          try {
            const taskStatus: any = await apiService.getTaskStatus(taskId);
            
            if (taskStatus.success && taskStatus.task) {
              const task = taskStatus.task;
              
              // 顯示進度信息
              if (task.status === 'running') {
                message.loading({
                  content: `${task.message}`,
                  //content: `${task.message} (${task.progress}/${task.total})`,
                  key: taskId,
                  duration: 0
                });
              } else if (task.status === 'completed') {
                clearInterval(pollInterval);
                message.destroy(taskId);
                message.success(`取貨日期更新完成！範圍: ${params.pickUpDateStart} 至 ${params.pickUpDateEnd}`);
                
                // Reload orders after completion
                loadOutboundOrders();
              } else if (task.status === 'failed') {
                clearInterval(pollInterval);
                message.destroy(taskId);
                message.error(`任務失敗: ${task.error || '未知錯誤'}`);
              }
            }
          } catch (error) {
            console.error('輪詢任務狀態失敗:', error);
            clearInterval(pollInterval);
            message.destroy(taskId);
            message.warning('無法獲取任務狀態，請稍後刷新頁面查看結果');
          }
        }, 2000); // 每2秒檢查一次
        
        // 設置最大輪詢時間（5分鐘）
        setTimeout(() => {
          clearInterval(pollInterval);
          message.destroy(taskId);
        }, 300000);
        
      } else {
        message.error('創建任務失敗');
      }
      
    } catch (error) {
      console.error('更新取貨日期失敗:', error);
      message.error('更新取貨日期失敗，請稍後重試');
    } finally {
      setUpdatePickUpDateLoading(false);
    }
  };

  // 廢除本地CSV組裝，改由後端生成（保留占位避免引用錯誤）
  const generateCSVContent = (_data: OutboundOrder[]) => '';

  // 獲取過濾後的出庫訂單數據（用於導出等功能）
  const getFilteredOutboundOrders = () => {
    // sortedAndFilteredOutboundOrders 已經包含了所有筛选逻辑（包括 hasStockIssues）
    // 這裡只需要返回排序後的數據即可
    return sortedAndFilteredOutboundOrders;
  };

  // 處理狀態變更
  const handleStatusChange = async (orderId: string, newStatus: OutboundOrderStatus) => {
    try {
      const target = outboundOrders.find(o => o.id === orderId);
      if (!target) return;
      if (newStatus !== '待發貨' && !target.deliveryTrackingNumber) {
        message.warning('請填寫配送單號');
        return;
      }
      if ((newStatus === '處理中' || newStatus === '已發貨') && target.hasStockIssues) {
        message.warning('庫存不足，無法更新為處理中/已發貨');
        return;
      }
      // 後端批量端點也支援單筆，並在處理中時建立批次與關聯（保證與批量行為一致）
      const isProcessing = newStatus === '處理中';
      const payload: any = { ids: [orderId], status: isProcessing ? 'processing' : mapZhStatusToBackend(newStatus), group: true, orderNumbers: [target.orderNumber] };
      const resp: any = await apiService.batchUpdateOutboundStatus(payload);

      // 本地回顯
      setOutboundOrders(prev => prev.map(order => order.id === orderId ? { ...order, status: newStatus, updatedAt: new Date().toISOString() } : order));
      if (isProcessing) {
        const batchNo = resp?.batch?.batchPickingNumber;
        if (batchNo) {
          Modal.success({ title: '已生成批量揀貨單', content: (
                <div>
                  <p>批次編號：{batchNo}</p>
                  <p>已將出庫單 {target.orderNumber} 加入該批次</p>
                </div>
            )});
        }
      }
      message.success(`訂單狀態已更新為 ${newStatus}`);
    } catch (e) {
      console.error(e);
      message.error('更新失敗');
      if (e instanceof Error) {
        message.error(e.message);
      }
    }
  };

  // 批量更新狀態
  const handleBatchStatusChange = async (newStatus: OutboundOrderStatus) => {
    try {
      if (!selectedRowKeys || selectedRowKeys.length === 0) {
        message.warning('請先選擇要更新的訂單');
        return;
      }
      const isProcessing = newStatus === '處理中';
      // 收集所有選中訂單的 orderNumber
      const selectedOrders = outboundOrders.filter(o => selectedRowKeys.includes(o.id));
      const orderNumbers = selectedOrders.map(o => o.orderNumber);
      const payload: any = { ids: selectedRowKeys, status: isProcessing ? 'processing' : mapZhStatusToBackend(newStatus), group: isProcessing, orderNumbers: orderNumbers };
      const resp: any = await apiService.batchUpdateOutboundStatus(payload);
      // 樂觀更新前端
      setOutboundOrders(prev => prev.map(o => selectedRowKeys.includes(o.id) ? { ...o, status: newStatus, updatedAt: new Date().toISOString() } : o));
      setSelectedRowKeys([]);
      if (isProcessing && resp?.batch?.batchPickingNumber) {
        Modal.success({ title: '已生成批量揀貨單', content: (
              <div>
                <p>批次編號：{resp.batch.batchPickingNumber}</p>
                <p>共 {selectedRowKeys.length} 張出庫單</p>
              </div>
          )});
      }
      message.success(`已批量更新 ${selectedRowKeys.length} 筆狀態為「${newStatus}」`);
    } catch (e) {
      console.error(e);
      message.error('批量更新失敗');
      if (e instanceof Error) {
        message.error(e.message);
      }
    }
  };

  // 批量狀態下拉菜單（與篩選區分的操作 UI）
  const batchStatusMenu = (
      <Menu onClick={({ key }) => handleBatchStatusChange(key as OutboundOrderStatus)}>
        <Menu.Item key="待發貨">待發貨</Menu.Item>
        <Menu.Item key="處理中">處理中</Menu.Item>
        <Menu.Item key="配貨中">配貨中</Menu.Item>
        <Menu.Item key="核對中">核對中</Menu.Item>
        <Menu.Item key="已包裝">已包裝</Menu.Item>
        <Menu.Item key="待攬收">待攬收</Menu.Item>
        <Menu.Item key="已發貨">已發貨</Menu.Item>
        <Menu.Item key="已收貨">已收貨</Menu.Item>
      </Menu>
  );


  // 批量狀態更新
  // const handleBatchStatusUpdate = (_orderIds: string[], _newStatus: OutboundOrderStatus) => {
  //   // 保留函數簽名以便後續實作，暫不使用
  // };

  // 處理退貨申請
  const handleReturnRequest = (order: OutboundOrder) => {
    if (order.status !== '已收貨') {
      message.warning('只有已收貨的訂單才能申請退貨');
      return;
    }
    setReturningOrder(order);
    setReturnModalVisible(true);
  };

  // 提交退貨處理
  const handleReturnSubmit = (orderId: string, returnInfo: any) => {
    setOutboundOrders(prev => prev.map(order => {
      if (order.id === orderId) {
        return {
          ...order,
          status: '退貨完成' as OutboundOrderStatus,
          returnInfo: returnInfo,
          updatedAt: new Date().toISOString()
        };
      }
      return order;
    }));

    // 如果選擇保留商品且已確認庫存調整，模擬庫存更新
    if (returnInfo.handlingStatus === 'keep' && returnInfo.inventoryAdjusted) {
      Modal.success({
        title: '庫存已更新',
        content: (
            <div>
              <p>退貨商品已成功補回庫存。</p>
              <p>訂單號: {outboundOrders.find(o => o.id === orderId)?.orderNumber}</p>
              <p>更新時間: {new Date().toLocaleString()}</p>
            </div>
        ),
      });
    }

    setReturnModalVisible(false);
    setReturningOrder(null);
  };

  // 處理批量上傳完成
  const handleBulkUploadComplete = async (uploadedOrders: any[]) => {
    try {
      const result: any = await apiService.bulkCreateOutboundOrders(uploadedOrders);
      if (result?.data && Array.isArray(result.data)) {
        await loadOutboundOrders();
        message.success(`成功導入 ${result.data.length} 個訂單`);
      } else {
        await loadOutboundOrders();
        message.success('批量導入完成');
      }
    } catch (e) {
      console.error('批量導入失敗', e);
      message.error('批量導入失敗，請重試');
    } finally {
      setBulkUploadVisible(false);
    }
  };

  // 定義所有可用的出庫訂單列
  const allOutboundColumns = {
    orderNumber: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('orderNumber')}
          >
            訂單號
            {getOutboundSortIcon('orderNumber')}
          </div>
      ),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 150,
      sorter: true,
      render: (orderNumber: string, record: OutboundOrder) => (
          <div>
            <Text strong style={{ color: record.hasStockIssues ? '#ff4d4f' : undefined }}>
              {orderNumber}
            </Text>
            {record.hasStockIssues && (
                <Tooltip title="庫存不足">
                  <WarningOutlined style={{ color: '#ff4d4f', marginLeft: 4 }} />
                </Tooltip>
            )}
          </div>
      ),
    },
    customerName: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('customerName')}
          >
            客戶名稱
            {getOutboundSortIcon('customerName')}
          </div>
      ),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 160,
      sorter: true,
      render: (text: string) => <Text strong>{text}</Text>,
    },
    customerCode: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('customerCode')}
          >
            客戶代碼
            {getOutboundSortIcon('customerCode')}
          </div>
      ),
      dataIndex: 'customerCode',
      key: 'customerCode',
      width: 120,
      sorter: true,
      render: (text: string) => <Text type="secondary">{text}</Text>,
    },
    receiver: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('receiver')}
          >
            收件人
            {getOutboundSortIcon('receiver')}
          </div>
      ),
      key: 'receiver',
      width: 120,
      sorter: true,
      render: (_: any, record: OutboundOrder) => (
          <div>
            <UserOutlined style={{ marginRight: 4 }} />
            {record.receiver}
          </div>
      ),
    },
    deliveryAddress: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('deliveryAddress')}
          >
            配送地址
            {getOutboundSortIcon('deliveryAddress')}
          </div>
      ),
      dataIndex: 'deliveryAddress',
      key: 'deliveryAddress',
      sorter: true,
      ellipsis: {
        showTitle: false,
      },
      render: (address: string) => (
          <Tooltip placement="topLeft" title={address}>
            {address}
          </Tooltip>
      ),
    },
    courier: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('courier')}
          >
            快遞商
            {getOutboundSortIcon('courier')}
          </div>
      ),
      dataIndex: 'courier',
      key: 'courier',
      width: 100,
      sorter: true,
      render: (courier: string) => (
          <Tag icon={<TruckOutlined />}>{courier}</Tag>
      ),
    },
    salesChannel: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('salesChannel')}
          >
            銷售渠道
            {getOutboundSortIcon('salesChannel')}
          </div>
      ),
      dataIndex: 'salesChannel',
      key: 'salesChannel',
      width: 120,
      sorter: true,
      render: (salesChannel: string) => (
          salesChannel ? (
              <Tag color="blue" icon={<GlobalOutlined />}>{salesChannel}</Tag>
          ) : '-'
      ),
    },
    pickUpDate: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('pickUpDate')}
          >
            取貨日期
            {getOutboundSortIcon('pickUpDate')}
          </div>
      ),
      dataIndex: 'pickUpDate',
      key: 'pickUpDate',
      width: 120,
      sorter: true,
      render: (pickUpDate: string) => (
          pickUpDate ? (
              <div>
                <div>{dayjs(pickUpDate).format('YYYY-MM-DD')}</div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {dayjs(pickUpDate).format('HH:mm')}
                </Text>
              </div>
          ) : '-'
      ),
    },
    trackingNumber: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('deliveryTrackingNumber')}
          >
            追蹤號
            {getOutboundSortIcon('deliveryTrackingNumber')}
          </div>
      ),
      dataIndex: 'deliveryTrackingNumber',
      key: 'deliveryTrackingNumber',
      width: 120,
      sorter: true,
      render: (trackingNumber: string) => (
          trackingNumber ? (
              <Text copyable={{ text: trackingNumber }}>
                {trackingNumber}
              </Text>
          ) : '-'
      ),
    },
    waybill: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('waybillNumber')}
          >
            運單號
            {getOutboundSortIcon('waybillNumber')}
          </div>
      ),
      dataIndex: 'waybillNumber',
      key: 'waybillNumber',
      width: 140,
      sorter: true,
      render: (wb: string) => (
          wb ? <Text copyable={{ text: wb }}>{wb}</Text> : '-'
      ),
    },
    status: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('status')}
          >
            狀態
            {getOutboundSortIcon('status')}
          </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      sorter: true,
      filters: [
        { text: '待發貨', value: '待發貨' },
        { text: '處理中', value: '處理中' },
        { text: '配貨中', value: '配貨中' },
        { text: '核對中', value: '核對中' },
        { text: '已包裝', value: '已包裝' },
        { text: '待攬收', value: '待攬收' },
        { text: '已發貨', value: '已發貨' },
        { text: '已收貨', value: '已收貨' },
        { text: '申請退貨', value: '申請退貨' },
        { text: '退貨處理中', value: '退貨處理中' },
        { text: '退貨完成', value: '退貨完成' },
      ],
      onFilter: (value: any, record: OutboundOrder) => record.status === value,
      render: (status: OutboundOrderStatus) => (
          <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
            {status}
          </Tag>
      ),
    },
    totalValue: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('totalValue')}
          >
            總金額
            {getOutboundSortIcon('totalValue')}
          </div>
      ),
      dataIndex: 'totalValue',
      key: 'totalValue',
      width: 100,
      sorter: true,
      render: (value: number) => (
          <Text strong>¥{(Number(value) || 0).toFixed(2)}</Text>
      ),
    },
    createdAt: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('createdAt')}
          >
            創建時間
            {getOutboundSortIcon('createdAt')}
          </div>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      sorter: true,
      render: (createdAt: string) => (
          <div>
            <div>{dayjs(createdAt).format('YYYY-MM-DD')}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {dayjs(createdAt).format('HH:mm:ss')}
            </Text>
          </div>
      ),
    },
    actions: {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right' as 'right',
      render: (_: any, record: OutboundOrder) => (
          <Space size="small" direction="vertical">
            <Space size="small">
              <Button
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => handleViewOrder(record)}
                  size="small"
              >
                詳情
              </Button>
              <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => handleEditOrder(record)}
                  size="small"
              >
                編輯
              </Button>
              <Popconfirm
                  title="確定要刪除此出庫訂單嗎？"
                  onConfirm={() => handleDeleteOrder(record)}
                  okText="確定"
                  cancelText="取消"
              >
                <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                >
                  刪除
                </Button>
              </Popconfirm>
            </Space>
            <Space direction="vertical" size="small">
              {record.status === '待攬收' && (
                  <Button
                      type="primary"
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={async () => {
                        const printWindow = window.open('', '_blank');
                        if (!printWindow) {
                          message.error('無法打開列印窗口，請檢查瀏覽器彈窗設置');
                          return;
                        }

                        // 先寫入簡單載入提示，避免 about:blank 殘留
                        printWindow.document.write(`<!DOCTYPE html><html><head><meta charset=\"utf-8\"><title>正在載入運單...</title></head><body style=\"font-family: Arial, \\\"Microsoft JhengHei\\\"; padding: 24px; color: #333;\">正在載入運單模板，請稍候...</body></html>`);
                        printWindow.document.close();

                        try {
                          const html = await apiService.generateWaybillHTML({ waybillNumber: record.deliveryTrackingNumber || undefined, orderNumber: record.orderNumber });
                          printWindow.document.open();
                          printWindow.document.write(html);
                          printWindow.document.close();
                          printWindow.onload = () => {
                            setTimeout(() => {
                              printWindow.print();
                              printWindow.onafterprint = () => {
                                printWindow.close();
                              };
                            }, 300);
                          };
                        } catch (e) {
                          message.error('載入運單模板失敗');
                          console.error(e);
                          try { printWindow.close(); } catch {}
                        }
                      }}
                  >
                    列印
                  </Button>
              )}
              {record.status !== '待攬收' && (
                  <Button
                      type="primary"
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={async () => {
                        if(record.waybillNumber) {
                          if(record.orderNumber.includes("H")) {// TODO HKTVmall【根據是否含有H 判斷是否是HKTVmall的 之後可以改（如果有其他規則）】
                            handleHktvMallDownloadPDF(record.orderNumber, record.waybillNumber)
                          }else {
                            handleDownloadPDF(record.orderNumber, record.waybillNumber)
                          }
                        }
                      }}
                  >
                    列印
                  </Button>
              )}
              {record.status === '待攬收' && (
                  <Button
                      type="link"
                      size="small"
                      icon={<FileTextOutlined />}
                      onClick={async () => {
                        let deliveryDate: string = dayjs().format('D/M/YYYY');
                        let deliveryTime: string = '11:00-16:00';
                        let deliveryItemNumber: string = '';
                        let receiverContact: string = '';
                        await new Promise<void>((resolve) => {
                          Modal.confirm({
                            title: '生成配送單',
                            content: (
                                <div style={{ marginTop: 8 }}>
                                  <div style={{ marginBottom: 8 }}>
                                    <Text>送貨日期</Text>
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        defaultValue={dayjs() as any}
                                        onChange={(d) => {
                                          deliveryDate = d ? dayjs(d).format('D/M/YYYY') : '';
                                        }}
                                    />
                                  </div>
                                  <div style={{ marginBottom: 8 }}>
                                    <Text>送貨時間</Text>
                                    <Input defaultValue={deliveryTime} onChange={(e) => (deliveryTime = e.target.value)} />
                                  </div>
                                  <div style={{ marginBottom: 8 }}>
                                    <Text>收件人聯絡電話</Text>
                                    <Input placeholder="可選" onChange={(e) => (receiverContact = e.target.value)} />
                                  </div>
                                  <div>
                                    <Text>送貨件數</Text>
                                    <Input placeholder="例如：2 箱" onChange={(e) => (deliveryItemNumber = e.target.value)} />
                                  </div>
                                </div>
                            ),
                            okText: '生成並列印',
                            cancelText: '取消',
                            onOk: () => {
                              resolve();
                            },
                          });
                        });

                        try {
                          const dnNumber = `DN-${record.orderNumber}-${dayjs().format('YYYYMMDDHHmmss')}`;
                          const printWindow = window.open('', '_blank');
                          if (!printWindow) {
                            message.error('無法打開列印窗口，請檢查瀏覽器彈窗設置');
                            return;
                          }
                          printWindow.document.write(`<!DOCTYPE html><html><head><meta charset=\"utf-8\"><title>正在載入配送單...</title></head><body style=\"font-family: Arial, \\\"Microsoft JhengHei\\\"; padding: 24px; color: #333;\">正在載入配送單模板，請稍候...</body></html>`);
                          printWindow.document.close();

                          const html = await apiService.generateDeliveryNoteHTML({
                            customerName: record.customerName,
                            deliveryDate,
                            deliveryTime,
                            deliveryOrderNumber: dnNumber,
                            receiverName: record.receiver,
                            receiverContact,
                            deliveryAddress: record.deliveryAddress,
                            deliveryItemNumber: deliveryItemNumber || '1 件',
                            orderNumber: record.orderNumber,
                          });
                          printWindow.document.open();
                          printWindow.document.write(html);
                          printWindow.document.close();
                          printWindow.onload = () => {
                            setTimeout(() => {
                              printWindow.print();
                              printWindow.onafterprint = () => {
                                printWindow.close();
                              };
                            }, 300);
                          };
                        } catch (e) {
                          message.error('載入配送單模板失敗');
                          console.error(e);
                        }
                      }}
                  >
                    配送單
                  </Button>
              )}
              <Select
                  size="small"
                  value={record.status}
                  onChange={(value) => handleStatusChange(record.id, value)}
                  style={{ width: 120 }}
                  disabled={record.hasStockIssues || record.status === '已收貨' || record.status?.includes('退貨')}
              >
                <Option value="待發貨">待發貨</Option>
                <Option value="處理中">處理中</Option>
                <Option value="配貨中">配貨中</Option>
                <Option value="核對中">核對中</Option>
                <Option value="已包裝">已包裝</Option>
                <Option value="待攬收">待攬收</Option>
                <Option value="已發貨">已發貨</Option>
                <Option value="已收貨">已收貨</Option>
              </Select>
              {record.status === '已收貨' && (
                  <Button
                      type="link"
                      size="small"
                      icon={<WarningOutlined />}
                      onClick={() => handleReturnRequest(record)}
                      style={{ color: '#fa8c16' }}
                  >
                    申請退貨
                  </Button>
              )}
              {record.returnInfo && (
                  <Tag color={record.status?.includes('退貨') ? 'red' : 'orange'}>
                    {record.returnInfo.handlingStatus === 'keep' ? '保留商品' :
                        record.returnInfo.handlingStatus === 'discard' ? '丟棄商品' :
                            record.returnInfo.handlingStatus === 'refund' ? '退款處理' : '換貨處理'}
                  </Tag>
              )}
            </Space>
          </Space>
      ),
    }
  };

  // 根據列配置生成顯示的出庫訂單列
  const columns: ColumnsType<OutboundOrder> = Object.entries(outboundColumnSettings)
      .filter(([_, config]: [string, any]) => config.visible)
      .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
      .map(([key]) => {
        const column = allOutboundColumns[key as keyof typeof allOutboundColumns];
        if (!column) {
          console.warn(`Missing outbound column definition for key: ${key}`);
        }
        return column;
      })
      .filter(Boolean); // 過濾掉undefined的列

  // 出庫主表支持拖拽列排序（排除 操作 列）
  const { columns: draggableColumns, order: outboundOrder } = useDraggableColumns<OutboundOrder>({
    columns: columns as any,
    disableDragKeys: ['actions'],
    storageKey: 'outbound-order-table',
  });

  // 無論拖拽/配置如何，強制將 操作 欄固定在最後
  const columnsWithActionsLast = useMemo(() => {
    const arr = (draggableColumns as any[]) || [];
    const idx = arr.findIndex((c: any) => c && (c.key === 'actions' || c.dataIndex === 'actions'));
    if (idx === -1) return arr;
    const copy = arr.slice();
    const [actionsCol] = copy.splice(idx, 1);
    if (actionsCol) {
      const originalRender = actionsCol.render;
      actionsCol.render = (text: any, record: OutboundOrder, index: number) => {
        const disabled = record.hasStockIssues;
        const tooltip = disabled ? '庫存不足，暫不可操作' : undefined;
        if (typeof originalRender === 'function') {
          const result = originalRender(text, record, index);
          if (React.isValidElement(result)) {
            return React.cloneElement(result as React.ReactElement, { disabled });
          }
        }
        return (
            <Space size="small">
              <Tooltip title={tooltip || '詳情'}>
                <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewOrder(record)} disabled={disabled} />
              </Tooltip>
              <Tooltip title={tooltip || '列印'}>
                <Button size="small" icon={<PrinterOutlined />} onClick={() => window.print()} disabled={disabled} />
              </Tooltip>
              <Tooltip title={tooltip || '編輯'}>
                <Button size="small" icon={<EditOutlined />} onClick={() => setSelectedOrder(record)} disabled={disabled} />
              </Tooltip>
              <Tooltip title={tooltip || '刪除'}>
                <Popconfirm
                    title="確認刪除?"
                    onConfirm={() => message.info('刪除功能暫未實作')}
                    okText="確認"
                    cancelText="取消"
                    disabled={disabled}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} disabled={disabled} />
                </Popconfirm>
              </Tooltip>
              <Tooltip title={tooltip || '變更狀態'}>
                <Select
                    value={record.status}
                    onChange={(value: OutboundOrderStatus) => message.info(`變更狀態：${value}`)}
                    style={{ width: 120 }}
                    disabled={disabled}
                >
                  {['待發貨','處理中','配貨中','核對中','已包裝','待攬收','已發貨','已收貨','申請退貨','退貨處理中','退貨完成'].map((s) => (
                      <Option key={s} value={s as any}>{s}</Option>
                  ))}
                </Select>
              </Tooltip>
            </Space>
        );
      };
    }
    copy.push(actionsCol);
    return copy;
  }, [draggableColumns]);

  // 拖拽後同步回列配置
  useEffect(() => {
    if (!outboundOrder || outboundOrder.length === 0) return;
    setOutboundColumnSettings((prev: any) => {
      const next: any = { ...prev };
      outboundOrder.forEach((k: string, idx: number) => {
        if (next[k]) next[k] = { ...next[k], order: idx };
      });
      Object.keys(next).forEach((k) => {
        if (!outboundOrder.includes(k)) {
          const o = next[k]?.order ?? 9999;
          next[k] = { ...next[k], order: Math.max(outboundOrder.length, o) };
        }
      });
      try {
        localStorage.setItem('outbound-order-columns-config', JSON.stringify(next));
        localStorage.setItem('outbound-order-columns-version', '2024-01-v4');
      } catch {}
      return next;
    });
  }, [outboundOrder]);

  // 處理出庫訂單列可見性切換
  const handleOutboundColumnVisibilityChange = (columnKey: string, visible: boolean) => {
    const newSettings = {
      ...outboundColumnSettings,
      [columnKey]: { ...outboundColumnSettings[columnKey], visible }
    };
    setOutboundColumnSettings(newSettings);
    localStorage.setItem('outbound-order-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('outbound-order-columns-version', '2024-01-v4');
  };

  // 顯示所有出庫訂單列
  const showAllOutboundColumns = () => {
    const allVisibleSettings = {
      orderNumber: { visible: true, order: 0 },
      customerName: { visible: true, order: 1 },
      customerCode: { visible: true, order: 2 },
      receiver: { visible: true, order: 3 },
      deliveryAddress: { visible: true, order: 4 },
      courier: { visible: true, order: 5 },
      salesChannel: { visible: true, order: 6 },
      trackingNumber: { visible: true, order: 7 },
      waybill: { visible: true, order: 8 },
      status: { visible: true, order: 9 },
      totalValue: { visible: true, order: 10 },
      createdAt: { visible: true, order: 11 },
      actions: { visible: true, order: 12 }
    };
    setOutboundColumnSettings(allVisibleSettings);
    localStorage.setItem('outbound-order-columns-config', JSON.stringify(allVisibleSettings));
    localStorage.setItem('outbound-order-columns-version', '2024-01-v5');
    message.success('所有列已顯示');
  };

  // 重置出庫訂單列配置
  const resetOutboundColumnSettings = () => {
    const defaultSettings = {
      orderNumber: { visible: true, order: 0 },
      customerName: { visible: true, order: 1 },
      customerCode: { visible: true, order: 2 },
      receiver: { visible: true, order: 3 },
      deliveryAddress: { visible: true, order: 4 },
      courier: { visible: true, order: 5 },
      salesChannel: { visible: true, order: 6 },
      trackingNumber: { visible: false, order: 7 },
      waybill: { visible: true, order: 8 },
      status: { visible: true, order: 9 },
      totalValue: { visible: true, order: 10 },
      createdAt: { visible: true, order: 11 },
      actions: { visible: true, order: 12 }
    };
    setOutboundColumnSettings(defaultSettings);
    localStorage.setItem('outbound-order-columns-config', JSON.stringify(defaultSettings));
    localStorage.setItem('outbound-order-columns-version', '2024-01-v5');
    message.success('列配置已重置');
  };

  // 出庫訂單列設置菜單
  const outboundColumnSettingsMenu = (
    <div style={{ 
      background: '#fff', 
      borderRadius: '2px', 
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>列設置</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
        {Object.entries(allOutboundColumns).map(([key, column]) => (
          <div 
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleOutboundColumnVisibilityChange(key, !(outboundColumnSettings[key]?.visible ?? true));
            }}
          >
            <Checkbox
              checked={outboundColumnSettings[key]?.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                handleOutboundColumnVisibilityChange(key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {typeof column.title === 'string' ? column.title :
                  key === 'orderNumber' ? '訂單號' :
                      key === 'customerName' ? '客戶名稱' :
                          key === 'customerCode' ? '客戶代碼' :
                              key === 'receiver' ? '收件人' :
                                  key === 'deliveryAddress' ? '配送地址' :
                                      key === 'courier' ? '快遞商' :
                                          key === 'salesChannel' ? '銷售渠道' :
                                              key === 'pickUpDate' ? '取貨日期' :
                                                  key === 'trackingNumber' ? '追蹤號' :
                                                      key === 'waybill' ? '運單號' :
                                                          key === 'status' ? '狀態' :
                                                              key === 'totalValue' ? '總金額' :
                                                                  key === 'createdAt' ? '創建時間' :
                                                                      key === 'actions' ? '操作' : key}
            </Checkbox>
          </div>
        ))}
      </div>
      <div style={{ borderTop: '1px solid #f0f0f0', padding: '4px 0' }}>
        <div 
          style={{ padding: '5px 12px', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            showAllOutboundColumns();
          }}
        >
          <Button type="link" size="small" style={{ padding: 0 }}>
            顯示所有列
          </Button>
        </div>
        <div 
          style={{ padding: '5px 12px', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            resetOutboundColumnSettings();
          }}
        >
          <Button type="link" size="small" style={{ padding: 0 }}>
            重置列設置
          </Button>
        </div>
      </div>
    </div>
  );

  // 處理出庫訂單數據排序
  const sortedAndFilteredOutboundOrders = useMemo(() => {
    let result = [...filteredOrders];

    if (outboundSortField && outboundSortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[outboundSortField as keyof OutboundOrder];
        let bValue: any = b[outboundSortField as keyof OutboundOrder];

        // 處理數字字段排序
        if (['totalValue'].includes(outboundSortField)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }

        // 處理日期字段排序
        if (['createdAt', 'pickUpDate'].includes(outboundSortField)) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }

        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return outboundSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return outboundSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [filteredOrders, outboundSortField, outboundSortOrder]);


  return (
      <div style={{ padding: '24px' }}>
        {/* 標題和展開收起按鈕 */}
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <Title level={2} style={{ marginBottom: 0 }}>出庫訂單管理</Title>
            <Text type="secondary">管理所有出庫訂單，包括創建新訂單、編輯現有訂單、追蹤發貨狀態等。系統會自動檢查庫存可用性並生成警告。</Text>
          </div>
          <Button
              type="text"
              icon={collapsed ? <DownOutlined /> : <UpOutlined />}
              onClick={toggleCollapsed}
              style={{ fontSize: 16 }}
          />
        </div>

        {/* KPI 儀表板 */}
        {!collapsed && (
            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={6}>
                <Card hoverable>
                  <Statistic
                      title="總訂單數"
                      value={statistics.total}
                      prefix={<ShoppingCartOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card hoverable>
                  <Statistic
                      title="待發貨"
                      value={statistics.pending}
                      prefix={<ClockCircleOutlined />}
                      valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card hoverable>
                  <Statistic
                      title="處理中"
                      value={statistics.processing}
                      prefix={<EditOutlined />}
                      valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card hoverable>
                  <Statistic
                      title="庫存問題"
                      value={statistics.stockIssues}
                      prefix={<WarningOutlined />}
                      valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
            </Row>
        )}

        {/* 操作和篩選區域 */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <Space direction="horizontal" wrap>
              <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddOrder}
              >
                新增出庫訂單
              </Button>
              <Button
                  icon={<UploadOutlined />}
                  onClick={() => setBulkUploadVisible(true)}
              >
                批量上傳
              </Button>
              <Button
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcel}
              >
                導出
              </Button>
              <Button
                  icon={<ReloadOutlined />}
                  onClick={() => loadOutboundOrders()}
                  loading={loading}
              >
                刷新數據
              </Button>
              <Button
                  icon={<ClockCircleOutlined />}
                  onClick={() => setUpdatePickUpDateModalVisible(true)}
              >
                Update Pick Up Date
              </Button>
              {/* 批量狀態變更（操作） */}
              <Dropdown overlay={batchStatusMenu} trigger={["click"]} disabled={!selectedRowKeys || selectedRowKeys.length === 0}>
                <Button type="primary" danger icon={<EditOutlined />}>批量更改狀態</Button>
              </Dropdown>
            </Space>
            <Space>
              <Input
                  placeholder="搜尋訂單號、運單號、收件人、地址..."
                  prefix={<SearchOutlined />}
                  value={filters.searchText}
                  onChange={(e) => setFilters({ ...filters, searchText: e.target.value })}
                  style={{ width: 300 }}
                  allowClear
              />
              <Dropdown 
                overlay={outboundColumnSettingsMenu} 
                trigger={['click']}
                open={outboundColumnSettingsVisible}
                onOpenChange={setOutboundColumnSettingsVisible}
              >
                <Button icon={<SettingOutlined />} title="列設置">
                  列設置
                </Button>
              </Dropdown>
            </Space>
          </div>

          {/* 篩選條件 */}
          <Row gutter={16}>
            <Col xs={24} sm={8} md={6} lg={4}>
              <Tooltip title="按當前處理進度篩選訂單（例如：待發貨/已發貨等）">
                <Select
                    placeholder="篩選狀態"
                    style={{ width: '100%' }}
                    value={filters.status}
                    onChange={(value) => setFilters({ ...filters, status: value })}
                    allowClear
                >
                  <Option value="待發貨">待發貨</Option>
                  <Option value="處理中">處理中</Option>
                  <Option value="配貨中">配貨中</Option>
                  <Option value="核對中">核對中</Option>
                  <Option value="已包裝">已包裝</Option>
                  <Option value="待攬收">待攬收</Option>
                  <Option value="已發貨">已發貨</Option>
                  <Option value="已收貨">已收貨</Option>
                  <Option value="申請退貨">申請退貨</Option>
                  <Option value="退貨處理中">退貨處理中</Option>
                  <Option value="退貨完成">退貨完成</Option>
                </Select>
              </Tooltip>
            </Col>
            <Col xs={24} sm={8} md={6} lg={4}>
              <Tooltip title="按是否有庫存問題進行篩選（有庫存問題/庫存正常）">
                <Select
                    placeholder="篩選庫存狀態"
                    style={{ width: '100%' }}
                    value={filters.hasStockIssues}
                    onChange={(value) => setFilters({ ...filters, hasStockIssues: value })}
                    allowClear
                >
                  <Option value="true">有庫存問題</Option>
                  <Option value="false">庫存正常</Option>
                </Select>
              </Tooltip>
            </Col>
            <Col xs={24} sm={8} md={6} lg={4}>
              <Tooltip title="按來源平台或渠道篩選（例如：HKTV Mall、Shopify、Amazon）">
                <Select
                    placeholder="篩選銷售渠道"
                    style={{ width: '100%' }}
                    value={filters.salesChannel}
                    onChange={(value) => setFilters({ ...filters, salesChannel: value })}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                >
                  <Option value="HKTV Mall">HKTV Mall</Option>
                  <Option value="Shopify">Shopify</Option>
                  <Option value="Amazon">Amazon</Option>
                  <Option value="eBay">eBay</Option>
                  <Option value="Taobao">Taobao</Option>
                </Select>
              </Tooltip>
            </Col>
          </Row>

          {/* 第二行篩選條件 */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Tooltip title="按取貨日期範圍篩選訂單">
                <DatePicker.RangePicker
                    style={{ width: '100%' }}
                    placeholder={['開始日期', '結束日期']}
                    value={filters.pickUpDateRange ? [dayjs(filters.pickUpDateRange[0]), dayjs(filters.pickUpDateRange[1])] : undefined}
                    onChange={(dates) => {
                      if (dates && dates[0] && dates[1]) {
                        setFilters({
                          ...filters,
                          pickUpDateRange: [
                            `${dates[0].startOf('day').format('YYYY-MM-DD')} 00:00:00`,
                            `${dates[1].endOf('day').format('YYYY-MM-DD')} 23:59:59.999`
                          ]
                        });
                      } else {
                        setFilters({ ...filters, pickUpDateRange: undefined });
                      }
                    }}
                    allowClear
                />
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Tooltip title="按店鋪ID篩選訂單（主要用於HKTV Mall訂單）- 可多選">
                <Select
                    mode="multiple"
                    placeholder="店鋪ID（可多選）"
                    style={{ width: '100%' }}
                    value={filters.storeId}
                    onChange={(value) => setFilters({ ...filters, storeId: value || [] })}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={hktvAccounts.map(account => ({
                      value: account.storeCode,
                      label: account.storeCode
                    }))}
                    maxTagCount="responsive"
                />
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Tooltip title="按 Run Tag 篩選訂單（根據 tag 關聯的 store codes 和 warehouse IDs 過濾）">
                <Select
                    placeholder="篩選 Run Tag"
                    style={{ width: '100%' }}
                    value={filters.runTagId}
                    onChange={(value) => setFilters({ ...filters, runTagId: value })}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                >
                  {runTags.map(tag => (
                    <Option key={tag.id} value={tag.id}>
                      {tag.tagName} ({tag.storeCodes.length} stores)
                    </Option>
                  ))}
                </Select>
              </Tooltip>
            </Col>
          </Row>
        </Card>

        {/* 出庫訂單表格 */}
        <Card>
          <Table
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                getCheckboxProps: (record) => ({ disabled: record.hasStockIssues })
              }}
              columns={columnsWithActionsLast as any}
              dataSource={sortedAndFilteredOutboundOrders}
              rowKey="id"
              loading={loading}
              pagination={{
                total: total,
                current: currentPage,
                pageSize: pageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: [10, 20, 50, 100, 500],
                showTotal: (total, range) =>
                    `顯示 ${range[0]}-${range[1]} 項，共 ${total} 項記錄`,
                onChange: (p, ps) => {
                  if (ps && ps !== pageSize) {
                    setPageSize(ps);
                    setCurrentPage(1);
                  } else {
                    setCurrentPage(p);
                  }
                }
              }}
              scroll={{ x: 1600 }}
              rowClassName={(record) => record.hasStockIssues ? 'row-stock-issue' : ''}
          />
        </Card>

        {/* 訂單詳情抽屜 */}
        <Drawer
            title="出庫訂單詳情"
            placement="right"
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            width={800}
        >
          {selectedOrder && (
              <div>
                {/* 基本信息 */}
                <Card title="訂單信息" style={{ marginBottom: 16 }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <p><strong>訂單號：</strong>{selectedOrder.orderNumber}</p>
                      <p><strong>客戶：</strong>{selectedOrder.customerName} ({selectedOrder.customerCode})</p>
                      <p><strong>收件人：</strong>{selectedOrder.receiver}</p>
                      <p><strong>配送地址：</strong>{selectedOrder.deliveryAddress}</p>
                    </Col>
                    <Col span={12}>
                      <p><strong>快遞商：</strong>{selectedOrder.courier}</p>
                      <p><strong>追蹤號：</strong>{selectedOrder.deliveryTrackingNumber || '-'}</p>
                      <p><strong>狀態：</strong>
                        <Tag color={getStatusColor(selectedOrder.status)} style={{ marginLeft: 8 }}>
                          {selectedOrder.status}
                        </Tag>
                      </p>
                      <p><strong>總金額：</strong>¥{(Number(selectedOrder.totalValue) || 0).toFixed(2)}</p>
                    </Col>
                  </Row>
                </Card>

                {/* 退貨信息 */}
                {selectedOrder.returnInfo && (
                    <Card title="退貨信息" style={{ marginBottom: 16 }}>
                      <Row gutter={16}>
                        <Col span={12}>
                          <p><strong>退貨原因：</strong>{selectedOrder.returnInfo.reason}</p>
                          <p><strong>申請時間：</strong>{dayjs(selectedOrder.returnInfo.requestDate).format('YYYY-MM-DD HH:mm:ss')}</p>
                          <p><strong>處理方式：</strong>
                            <Tag color={
                              selectedOrder.returnInfo.handlingStatus === 'keep' ? 'green' :
                                  selectedOrder.returnInfo.handlingStatus === 'discard' ? 'red' :
                                      selectedOrder.returnInfo.handlingStatus === 'refund' ? 'orange' : 'blue'
                            }>
                              {selectedOrder.returnInfo.handlingStatus === 'keep' ? '保留商品' :
                                  selectedOrder.returnInfo.handlingStatus === 'discard' ? '丟棄商品' :
                                      selectedOrder.returnInfo.handlingStatus === 'refund' ? '退款處理' : '換貨處理'}
                            </Tag>
                          </p>
                        </Col>
                        <Col span={12}>
                          <p><strong>商家確認：</strong>{selectedOrder.returnInfo.merchantConfirmation || '待確認'}</p>
                          <p><strong>處理人員：</strong>{selectedOrder.returnInfo.confirmedBy || '-'}</p>
                          <p><strong>庫存調整：</strong>
                            {selectedOrder.returnInfo.inventoryAdjusted ?
                                <Tag color="green">已調整</Tag> :
                                <Tag color="orange">未調整</Tag>}
                          </p>
                        </Col>
                      </Row>
                      {selectedOrder.returnInfo.handlingRemark && (
                          <div>
                            <Divider />
                            <p><strong>處理備註：</strong></p>
                            <Text>{selectedOrder.returnInfo.handlingRemark}</Text>
                          </div>
                      )}
                    </Card>
                )}

                {/* 產品清單 */}
                <Card title="產品清單">
                  <Table
                      dataSource={selectedOrder.items}
                      rowKey="id"
                      size="small"
                      pagination={false}
                      columns={[
                        {
                          title: '產品',
                          key: 'product',
                          render: (_, item) => (
                              <div>
                                <div style={{ fontWeight: 500 }}>{item.productNameEN}</div>
                                <div style={{ fontSize: '12px', color: '#666' }}>{item.productNameCN}</div>
                                <div style={{ fontSize: '12px', color: '#999' }}>SKU: {item.skuId}</div>
                              </div>
                          ),
                        },
                        {
                          title: '數量',
                          key: 'quantity',
                          width: 120,
                          render: (_, item) => (
                              <div>
                                <div>包裝: {item.quantityPackage}</div>
                                <div>基本單位: {item.quantityBaseUnit}</div>
                              </div>
                          ),
                        },
                        {
                          title: '庫存狀態',
                          key: 'stock',
                          width: 120,
                          render: (_, item) => (
                              <div>
                                <div>可用: {item.availableStock}</div>
                                {item.isStockInsufficient && (
                                    <Tag color="red">庫存不足</Tag>
                                )}
                              </div>
                          ),
                        },
                        {
                          title: '單價',
                          dataIndex: 'unitPrice',
                          width: 100,
                          render: (price) => `¥${(Number(price) || 0).toFixed(2)}`,
                        },
                      ]}
                  />
                </Card>
              </div>
          )}
        </Drawer>

        {/* 新增/編輯表單 */}
        <OutboundOrderForm
            visible={formVisible}
            order={editingOrder}
            customers={customers}
            products={mockProducts}
            couriers={mockCouriers}
            countryCodes={mockCountryCodes}
            allocateRules={allocateRules}
            warehouses={warehouses}
            onSubmit={handleFormSubmit}
            onCancel={() => setFormVisible(false)}
        />

        {/* 批量上傳彈窗 */}
        <OutboundBulkUploadModal
            visible={bulkUploadVisible}
            onCancel={() => setBulkUploadVisible(false)}
            onUploadComplete={handleBulkUploadComplete}
        />

        {/* 退貨處理彈窗 */}
        <ReturnOrderModal
            visible={returnModalVisible}
            order={returningOrder}
            onCancel={() => {
              setReturnModalVisible(false);
              setReturningOrder(null);
            }}
            onSubmit={handleReturnSubmit}
        />

        {/* Update Pick Up Date Modal */}
        <Modal
            title="Update Pick Up Date"
            open={updatePickUpDateModalVisible}
            onOk={handleUpdatePickUpDate}
            onCancel={() => {
              setUpdatePickUpDateModalVisible(false);
              setUpdatePickUpDateRange(null);
            }}
            confirmLoading={updatePickUpDateLoading}
            okText="更新"
            cancelText="取消"
        >
          <div style={{ marginBottom: 16 }}>
            <Text>請選擇取貨日期範圍（最多7天）：</Text>
          </div>
          <DatePicker.RangePicker
              style={{ width: '100%' }}
              placeholder={['開始日期', '結束日期']}
              value={updatePickUpDateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  const daysDiff = dates[1].diff(dates[0], 'days');
                  if (daysDiff > 7) {
                    message.warning('日期範圍不能超過7天');
                    return;
                  }
                  setUpdatePickUpDateRange([dates[0], dates[1]]);
                } else {
                  setUpdatePickUpDateRange(null);
                }
              }}
              disabledDate={(current) => {
                if (!updatePickUpDateRange || !updatePickUpDateRange[0]) {
                  return false;
                }
                const startDate = updatePickUpDateRange[0];
                const maxDate = startDate.add(7, 'days');
                const minDate = startDate.subtract(7, 'days');
                return current && (current > maxDate || current < minDate);
              }}
          />
        </Modal>

        <style>{`
        .row-stock-issue {
          background-color: #fff2f0 !important;
        }
        .row-stock-issue:hover {
          background-color: #ffa39e !important;
        }
      `}</style>
      </div>
  );
};

export default OutboundOrderPage;